<script setup lang="ts">
import InternalLinks from "@/components/seo/InternalLinks.vue";
import { seoConfig } from "@/config/seo.config";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

useHead({
  title: seoConfig.dua.title,
  meta: [
    {
      name: "description",
      content: seoConfig.dua.description,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: seoConfig.dua.canonical,
    },
  ],
});

const { messages, locale } = useI18n();
const { generateWebPageSchema, addSchema } = useSchemaMarkup();

const duaPage = computed(() => {
  return messages.value[locale.value]?.pages?.dua;
});

type DuaCategoryKey = "mostRead" | "daily" | "ashra" | "last10" | "quranic";

const activeCategory = ref<DuaCategoryKey>("mostRead");

const categories = computed(() => {
  return duaPage.value?.categories ?? [];
});

const filteredDuas = computed(() => {
  const duas = duaPage.value?.duas ?? [];
  return duas.filter(
    (dua: { category: DuaCategoryKey }) => dua.category === activeCategory.value,
  );
});
const categoryCounts = computed(() => {
  const duas = duaPage.value?.duas ?? [];
  const counts: Record<string, number> = {};

  for (const dua of duas as Array<{ category: DuaCategoryKey }>) {
    counts[dua.category] = (counts[dua.category] ?? 0) + 1;
  }

  return counts;
});

watch(
  categories,
  (list) => {
    if (!list.length) return;
    const exists = list.some(
      (item: { key: DuaCategoryKey }) => item.key === activeCategory.value,
    );
    if (!exists) {
      activeCategory.value = list[0].key;
    }
  },
  { immediate: true },
);

const webPageSchema = computed(() =>
  generateWebPageSchema({
    name: seoConfig.dua.title,
    description: seoConfig.dua.description,
    url: seoConfig.dua.canonical,
  }),
);

const buildWhatsAppShareUrl = (dua: {
  title: string;
  arabic: string;
  romanUrdu: string;
  meaning: string;
}) => {
  const shareText = `${dua.title}\n\n${dua.arabic}\n\n${dua.romanUrdu}\n\n${dua.meaning}\n\n${seoConfig.dua.canonical}`;
  return `https://wa.me/?text=${encodeURIComponent(shareText)}`;
};

onMounted(() => {
  addSchema(webPageSchema.value);
});
</script>

<template>
  <section
    v-if="duaPage"
    class="w-full max-w-full sm:max-w-2xl mx-auto px-3 sm:px-4 py-6 space-y-6"
  >
    <!-- Intro -->
    <header class="space-y-2">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
        {{ duaPage.title }}
      </h1>
      <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
        {{ duaPage.intro }}
      </p>
    </header>

    <div class="flex flex-wrap gap-1.5">
      <button
        v-for="chip in categories"
        :key="chip.key"
        class="px-3 py-1 rounded-full text-xs whitespace-nowrap"
        :class="
          activeCategory === chip.key
            ? 'bg-gray-800 text-white dark:bg-emerald-500 dark:text-white'
            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200'
        "
        @click="activeCategory = chip.key"
      >
        {{ chip.label }}
        <span
          class="text-xs ml-1"
          :class="
            activeCategory === chip.key
              ? 'text-gray-200 dark:text-emerald-100'
              : 'text-gray-500 dark:text-gray-400'
          "
        >
          ({{ categoryCounts[chip.key] ?? 0 }})
        </span>
      </button>
    </div>

    <!-- Dua Cards -->
    <div class="space-y-4">
      <div
        v-for="(dua, index) in filteredDuas"
        :key="index"
        class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3 bg-white dark:bg-gray-800"
      >
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-base font-medium text-gray-800 dark:text-gray-100">
            {{ dua.title }}
          </h2>
          <span
            v-if="dua.tag"
            class="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
          >
            {{ dua.tag }}
          </span>
        </div>

        <p
          class="text-3xl text-gray-900 dark:text-gray-100 leading-relaxed"
          :class="dua.category === 'quranic' ? 'font-quran' : 'font-arabic'"
        >
          {{ dua.arabic }}
        </p>

        <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          {{ dua.romanUrdu }}
        </p>

        <div class="space-y-1">
          <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
            {{ dua.meaning }}
          </p>

          <div class="flex justify-end items-center">
            <a
              :href="buildWhatsAppShareUrl(dua)"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Share Dua on WhatsApp"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-3.5 h-3.5"
                aria-hidden="true"
              >
                <path
                  d="M12.04 2C6.55 2 2.1 6.44 2.1 11.93c0 1.76.46 3.48 1.34 5L2 22l5.22-1.37a9.9 9.9 0 0 0 4.82 1.23h.01c5.48 0 9.94-4.44 9.94-9.93S17.53 2 12.04 2Zm5.79 14.1c-.24.67-1.36 1.25-1.89 1.33-.49.07-1.12.1-1.8-.11-.41-.13-.94-.31-1.62-.61-2.85-1.23-4.7-4.1-4.84-4.29-.14-.19-1.16-1.54-1.16-2.94s.73-2.09.99-2.38c.26-.28.57-.35.76-.35h.55c.17 0 .4-.06.62.47.24.58.83 2 .9 2.14.07.14.12.31.02.5-.1.19-.16.31-.31.48-.16.17-.33.38-.47.51-.16.16-.32.33-.14.64.18.31.8 1.32 1.72 2.14 1.18 1.05 2.17 1.38 2.48 1.53.31.16.49.14.67-.08.19-.23.79-.92 1-1.24.21-.31.43-.26.72-.16.3.1 1.86.88 2.18 1.03.31.16.52.24.59.38.07.14.07.81-.17 1.48Z"
                />
              </svg>
              <span>Share Dua</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <InternalLinks current="dua" />
  </section>
</template>
